import React, { useState, useRef, useEffect } from "react";
import toast from "react-hot-toast";
import { useQuery } from "react-query";
import Pagination from "../../../../../components/pagination/Pagination";
import ConfirmModal from "../../../../../components/Modal/ConfirmModal";
import { api } from "../../../../../api/notification";
import { ScaleLoader } from "react-spinners";
import moment from "moment";
import VerticalMenu from "../../../../../components/dropdown/VerticalMenu";
import MenuItem from "../../../../../components/dropdown/MenuItem";
import Search from "../../../../../components/Inputs/Search";
import Select from "react-select";
import CreateNotification from "./CreateNotification";
import { BiSort } from "react-icons/bi";
import { MdOutlineFilterList } from "react-icons/md";
import DatePicker from "react-datepicker"; // Import React DatePicker
import "react-datepicker/dist/react-datepicker.css";
import PreviewModal from "./PreviewModal";

const StatsCard = ({ title, value, change, icon }) => (
  <div className="bg-white p-4 shadow rounded-lg flex flex-col  space-y-3">
    <div className="flex justify-between ">
      <div className="text-gray-500 text-xs text-wrap">{title}</div>
      <div className="text-gray-500 text-xs">{icon}</div>
    </div>
    <div className="text-base font-bold">{value}</div>
    <div className="text-sm text-green-500">{change}</div>
  </div>
);

const NotificationsTemplates = ({}) => {
  const user = JSON.parse(window.sessionStorage.getItem("user"));
  const orgId = user?.org?.id;

  const [deleteModal, setDeleteModal] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [eventId, setEventId] = useState(null);
  const [openNotificationModal, setOpenNotificationModal] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [order, setOrder] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [filterBy, setFilterBy] = useState("");
  const [dateScheduled, setDateScheduled] = useState(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  const sortRef = useRef(null);
  const filterRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleSortDropdown = () => {
    setShowSortDropdown((prev) => !prev);
  };

  const toggleFilterDropdown = () => {
    setShowFilterDropdown((prev) => !prev);
  };

  const handleSortChange = (field) => {
    setSortBy(field);
    setShowSortDropdown(false);
    refetch();
  };

  const handleOrderChange = (newOrder) => {
    setOrder(newOrder);
    setShowSortDropdown(false);
    refetch();
  };

  const handleFilterChange = (field) => {
    setFilterBy(field);
    if (field !== "dateScheduled") {
      setDateScheduled(null);
    }
    setShowFilterDropdown(false);
    refetch();
  };

  const handleDateChange = (date) => {
    setDateScheduled(date);
    refetch();
  };

  const { data: events } = useQuery(
    ["event", orgId],
    async () => {
      const response = await api.getEventTriggers();
      return response.data;
    },
    {
      staleTime: 5 * 60 * 1000, // data is considered fresh for 5 minutes
      cacheTime: 30 * 60 * 1000, // data is cached for 30 minutes after becoming stale
    },
    {
      onError: (error) => {
        toast.error(error.response.data.message);
      },
    }
  );

  const {
    data: notifications,
    isLoading,
    refetch,
  } = useQuery(
    [
      "all-notifications",
      orgId,
      search,
      perPage,
      currentPage,
      eventId,
      order,
      sortBy,
      dateScheduled,
    ],
    async () => {
      const response = await api.getAllTemplates({
        orgId,
        search,
        perPage,
        currentPage,
        eventId,
        order,
        sortBy,
        dateScheduled: dateScheduled ? dateScheduled.toISOString() : "",
      });
      return response.data;
    },
    {
      cacheTime: 30000,
      staleTime: 30000,
      onError: (error) => {
        toast.error(error.response.data.message);
      },
    }
  );

  const { data: summaryData, refetch: refecthSummaryData } = useQuery(
    ["notifications-summary"],
    async () => {
      const response = await api.getNotificationSummary({
        orgId,
      });

      return response.data;
    },
    {
      cacheTime: 30000,
      staleTime: 30000,
    },
    {
      onError: (error) => {
        toast.error(error.response.data.message);
      },
    }
  );

  const options = events?.map((event) => ({
    value: event.id,
    label: event.name,
  }));

  const handleChange = (selectedOption) => {
    setEventId(selectedOption.value);
  };

  const deleteAdmin = async () => {
    if (!selectedUser) return;
    try {
      const response = await api.deleteNotificationTemplate({
        orgId: orgId,
        notificationId: selectedUser.id,
      });
      setDeleteModal(false);
      toast.success(response.message);
      setSelectedUser(null);
      refetch();
      refecthSummaryData(); // Refresh the data after deletion
    } catch (error) {
      toast.error(error.response.data.message);
    }
  };

  const sendNotification = async () => {
    if (!selectedUser) return;
    toast.loading("Sending notification...");
    try {
      const response = await api.sendNotification({
        orgId: orgId,
        notificationId: selectedUser.id,
      });
      toast.dismiss();
      toast.success(response.message, {
        duration: 5000, // Display the toast for 2 seconds
      });
      refecthSummaryData();
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response.data.message);
      toast.dismiss();
    }
  };

  return (
    <div className="p-5 sm:p-10">
      <div className="flex justify-between">
        <h1 className="text-lg font-semibold text-[#091F46]">Notification</h1>
        <button
          type="button"
          onClick={() => setOpenNotificationModal(true)}
          className="p-3 bg-customBlue text-white text-sm rounded-md"
        >
          <i className="bi bi-plus-lg"></i> Create Notification
        </button>
      </div>

      <div className="flex-1 p-4 space-y-4">
        <div className="grid grid-cols-4 gap-4 mb-10">
          <StatsCard
            title="Total Notifications Sent"
            icon={<i className="bi bi-envelope"></i>}
            value={summaryData?.totalNotificationsSent ?? 0}
            change={summaryData?.openedNotifications ?? 0}
          />
          <StatsCard
            title="Open Rate"
            icon={<i className="bi bi-envelope-open-fill"></i>}
            value={summaryData?.openRate ?? "0%"}
            change={"0%"}
          />
          <StatsCard
            title="Engagement Actions"
            icon={<i className="bi bi-graph-up-arrow"></i>}
            value={summaryData?.engagementActions ?? 0}
            change={summaryData?.engagementRate ?? 0}
          />
          <StatsCard
            title="Delivery Failures"
            icon={<i className="bi bi-exclamation-triangle-fill"></i>}
            value={summaryData?.deliveryFailures ?? 0}
            change={summaryData?.deliveryFailuresRate ?? 0}
          />
        </div>

        <div className="flex justify-between items-center w-full">
          <div className="w-1/2 ">
            <Search
              onChange={setSearch}
              placeholder={"Search ..."}
              className={"w-full py-2"}
            />
          </div>

          <div className="flex items-center space-x-2">
            {/* Sort Icon with Dropdown */}
            <div className="relative" ref={sortRef}>
              <div
                className="p-2 bg-white rounded cursor-pointer"
                onClick={toggleSortDropdown}
              >
                <BiSort className="h-5 w-5" />
              </div>
              {showSortDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white shadow-lg rounded-md p-2 z-50">
                  <div className="font-bold mb-2">Sort By</div>
                  {/* {["name", "createdAt", "updatedAt", "dateScheduled"].map(
                    (field) => (
                      <div
                        key={field}
                        className={`p-2 cursor-pointer hover:bg-gray-100 ${
                          sortBy === field ? "font-bold" : ""
                        }`}
                        onClick={() => handleSortChange(field)}
                      >
                        {field.replace(/([A-Z])/g, " $1").trim()}
                      </div>
                    )
                  )} */}
                  <div className="border-t mt-2 pt-2 font-bold">Order</div>
                  <div
                    className="p-2 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleOrderChange("asc")}
                  >
                    🔼 A-Z (Ascending)
                  </div>
                  <div
                    className="p-2 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleOrderChange("desc")}
                  >
                    🔽 Z-A (Descending)
                  </div>
                </div>
              )}
            </div>

            {/* Filter Icon with Dropdown */}
            <div className="relative" ref={filterRef}>
              <div
                className="p-2 bg-white rounded cursor-pointer"
                onClick={toggleFilterDropdown}
              >
                <MdOutlineFilterList className="h-5 w-5" />
              </div>
              {showFilterDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white shadow-lg rounded-md p-2 z-50">
                  <div className="font-bold mb-2">Filter By Date</div>

                  <div
                    className={`p-2 cursor-pointer hover:bg-gray-100 ${
                      filterBy === "dateScheduled" ? "font-bold" : ""
                    }`}
                    onClick={() => handleFilterChange("dateScheduled")}
                  >
                    Date Scheduled
                  </div>

                  {filterBy === "dateScheduled" && (
                    <div className="mt-2">
                      <DatePicker
                        selected={dateScheduled}
                        onChange={handleDateChange}
                        dateFormat="yyyy-MM-dd"
                        className="border p-2 w-full"
                        placeholderText="Select Date"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <Select
              options={options}
              onChange={handleChange}
              className="w-1/2"
            />
          </div>
        </div>

        <div className="bg-white p-3 rounded-lg shadow-sm">
          {isLoading ? (
            <div className="flex justify-center items-center">
              <ScaleLoader color="#FF9100" />
            </div>
          ) : (
            <div className="">
              <table className="table-container">
                <thead className="table-header">
                  <tr>
                    <th className="table-head text-left">No</th>
                    <th className="table-head text-left">Name</th>
                    <th className="table-head text-left">Event</th>
                    {/* <th className="table-head">Created at</th> */}
                    <th className="table-head text-left">Last Updated</th>
                  </tr>
                </thead>

                <tbody className="table-body text-xs">
                  {notifications?.length > 0 ? (
                    notifications.map((admin, index) => (
                      <tr key={index}>
                        <td className="table-data">{index + 1}</td>
                        <td className="table-data text-left">{admin.name}</td>

                        <td className="table-data text-left">
                          {admin.eventTrigger?.name}
                        </td>

                        {/* <td className="table-data ">
                            {moment(admin.createdAt).format(
                              "YYYY/MM/DD, h:mm a"
                            )}
                          </td> */}
                        <td className="table-data ">
                          {moment(admin.updatedAt).format("YYYY/MM/DD, h:mm a")}
                        </td>
                        <td className="table-data text-center z-10">
                          <VerticalMenu>
                            <MenuItem
                              icon={
                                <i className="bi bi-eye-fill cursor-pointer"></i>
                              }
                              label="Preview"
                              onClick={() => {
                                setSelectedUser(admin);
                                setOpenPreviewModal(true);
                              }}
                            />
                            <MenuItem
                              icon={
                                <i className="bi bi-pencil-fill cursor-pointer"></i>
                              }
                              label="Edit"
                              onClick={() => {
                                setSelectedUser(admin);
                                setOpenNotificationModal(true);
                              }}
                            />

                            <MenuItem
                              icon={
                                <i className="bi bi-trash3-fill  text-red-500 cursor-pointer"></i>
                              }
                              label="Delete"
                              onClick={() => {
                                setSelectedUser(admin);
                                setDeleteModal(true);
                              }}
                            />
                            <MenuItem
                              icon={
                                <i class="bi bi-send-arrow-up-fill cursor-pointer"></i>
                              }
                              label="Send"
                              onClick={() => {
                                setSelectedUser(admin);
                                sendNotification();
                              }}
                            />
                          </VerticalMenu>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="table-data" colSpan="12">
                        <p className="text-center">No Data Available</p>
                      </td>
                    </tr>
                  )}
                </tbody>
                {deleteModal && (
                  <ConfirmModal
                    title="Delete Admin"
                    description={`Are you sure you want to delete ‘‘${selectedUser?.name}”?`}
                    onYes={deleteAdmin}
                    onClose={() => setDeleteModal(false)}
                  />
                )}
              </table>
            </div>
          )}
          {notifications?.length > 10 && (
            <section className={`mt-1 px-3 rouded-lg bg-white`}>
              <Pagination
                data={notifications?.meta}
                perPage={perPage}
                pageNo={currentPage}
                setPageNo={setCurrentPage}
                setPerPage={setPerPage}
              />
            </section>
          )}

          {openNotificationModal && (
            <CreateNotification
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              openNotificationModal={openNotificationModal}
              setOpenNotificationModal={setOpenNotificationModal}
              refetch={refetch}
              refecthSummaryData={refecthSummaryData}
            />
          )}
          {/* {openPreviewModal && ( */}
            <PreviewModal
              openPreviewModal={openPreviewModal}
              setOpenPreviewModal={setOpenPreviewModal}
              selectedUser={selectedUser}
              refetch={refetch}
            />
          {/* )} */}
        </div>
      </div>
    </div>
  );
};

export default NotificationsTemplates;
